import Accordion from "@/components/Accordion";
import React from "react";
import LogoUpload from "../../LogoUpload";
import ColorPicker from "@/components/ColorPicker";
import Slider from "@/components/Slider";
import { StyleConfigModel } from "@/pages/Messaging/types";
import { Checkbox, LoadingSpinner, Toggle } from "@velaro/velaro-component-lib";
import { ChatWindow } from "../../types";

interface Props {
  onExpanded(): void;
  workingChatWindow: ChatWindow;
  setWorkingChatWindow: (chatWindow: ChatWindow) => void;
  setStagedLogoFile: (file: File | null) => void;
}

export function Branding({
  onExpanded,
  workingChatWindow,
  setWorkingChatWindow,
  setStagedLogoFile
}: Props) {
  const workingStyleConfig = workingChatWindow.styleConfig || {};

  const updateStyleConfig = (updates: Partial<StyleConfigModel>) => {
    setWorkingChatWindow({
      ...workingChatWindow,
      styleConfig: {
        ...workingChatWindow.styleConfig,
        ...updates
      }
    });
  };

  return (
    <Accordion.Item eventKey="branding" onExpanded={() => onExpanded()}>
      <Accordion.Header>
        <Accordion.Title>Branding</Accordion.Title>
        <Accordion.SubTitle>
          Customize the chat window to reflect your brand identity
        </Accordion.SubTitle>
      </Accordion.Header>
      <Accordion.Body>
        <div>
          <LogoUpload
            setStageLogoFile={setStagedLogoFile}
            update={updateStyleConfig}
            workingStyleConfig={workingStyleConfig}
            field="logoUrl"
            label="Logo"
          />
          <div className="my-4 border-t border-slate-200" />
          <div className="text-sm font-medium mb-2">Color</div>
          <div className="grid grid-cols-3 items-center gap-4 mb-3">
            <div className="text-sm font-normal">Primary Color</div>
            <ColorPicker
              value={workingStyleConfig.backgroundColor}
              onChange={(value) =>
                updateStyleConfig({ backgroundColor: value })
              }
            />
            <div />
          </div>

          <div className="grid grid-cols-3 items-center gap-4 mb-3">
            <div className="text-sm font-normal">Header Text Color</div>
            <ColorPicker
              value={workingStyleConfig.headerTextColor || ""}
              onChange={(value) =>
                updateStyleConfig({ headerTextColor: value })
              }
              disabled={workingStyleConfig.useContrastedColorHeader}
            />
            <Checkbox
              checked={workingStyleConfig.useContrastedColorHeader}
              onClick={() =>
                updateStyleConfig({
                  useContrastedColorHeader:
                    !workingStyleConfig.useContrastedColorHeader
                })
              }
              label="Use Contrast Color"
            />
          </div>

          <div className="grid grid-cols-3 items-center gap-4 mb-3">
            <div className="text-sm font-normal">Body Text Color</div>
            <ColorPicker
              value={workingStyleConfig.bodyTextColor || ""}
              onChange={(value) => updateStyleConfig({ bodyTextColor: value })}
              disabled={workingStyleConfig.useContrastedColorBody}
            />
            <Checkbox
              checked={workingStyleConfig.useContrastedColorBody}
              onClick={() =>
                updateStyleConfig({
                  useContrastedColorBody:
                    !workingStyleConfig.useContrastedColorBody
                })
              }
              label="Use Contrast Color"
            />
          </div>

          <div className="my-4 border-t border-slate-200" />
          <div className="text-sm font-medium mb-2">Font</div>
          <select
            className="border border-slate-300 rounded-md p-2 w-full text-sm mr-1"
            value={workingStyleConfig.fontFamily ?? "inherit"}
            onChange={(e) => updateStyleConfig({ fontFamily: e.target.value })}
          >
            <option value="inherit">Default (inherit from page)</option>
            <option value="'Source Sans Pro', sans-serif">Source Sans Pro</option>
            <option value="'Inter', sans-serif">Inter</option>
            <option value="'Roboto', sans-serif">Roboto</option>
            <option value="'Open Sans', sans-serif">Open Sans</option>
            <option value="'Lato', sans-serif">Lato</option>
            <option value="'Poppins', sans-serif">Poppins</option>
            <option value="'Nunito', sans-serif">Nunito</option>
            <option value="Arial, sans-serif">Arial</option>
            <option value="Georgia, serif">Georgia</option>
          </select>

          <div className="my-4 border-t border-slate-200" />
          <div className="text-sm font-medium mb-2">Frame</div>
          <div className="grid grid-cols-3 items-center gap-4 mb-3">
            <div className="text-sm font-normal">Frame Background Color</div>
            <ColorPicker
              value={workingStyleConfig.frameBackgroundColor ?? "#EBEBEB"}
              onChange={(value) =>
                updateStyleConfig({ frameBackgroundColor: value })
              }
            />
            <div />
          </div>
          <div className="grid grid-cols-3 items-center gap-4 mb-3">
            <div className="text-sm font-normal">Frame Text Color</div>
            <ColorPicker
              value={workingStyleConfig.frameTextColor ?? "#2F3742"}
              onChange={(value) =>
                updateStyleConfig({ frameTextColor: value })
              }
            />
            <div />
          </div>
          <div className="flex justify-between items-center mt-2">
            <div className="text-sm font-normal">Enable Popout Button</div>
            <Toggle
              toggled={workingStyleConfig.enablePopout ?? false}
              changeToggle={() =>
                updateStyleConfig({
                  enablePopout: !(workingStyleConfig.enablePopout ?? false)
                })
              }
            />
          </div>

          <div className="my-4 border-t border-slate-200" />
          <div className="text-sm font-medium mb-2">Chat Bubbles</div>
          <div className="grid grid-cols-4 gap-2 items-center text-xs text-slate-400 mb-1">
            <div></div>
            <div>Background</div>
            <div>Text</div>
            <div>Border</div>
          </div>
          <div className="grid grid-cols-4 items-center gap-2 mb-3">
            <div className="text-sm font-normal">Agent</div>
            <ColorPicker
              value={workingStyleConfig.agentBubbleBgColor ?? "#FFFFFF"}
              onChange={(value) =>
                updateStyleConfig({ agentBubbleBgColor: value })
              }
            />
            <ColorPicker
              value={workingStyleConfig.agentBubbleTextColor ?? "#161616"}
              onChange={(value) =>
                updateStyleConfig({ agentBubbleTextColor: value })
              }
            />
            <ColorPicker
              value={workingStyleConfig.agentBubbleBorderColor ?? "#E1E1E1"}
              onChange={(value) =>
                updateStyleConfig({ agentBubbleBorderColor: value })
              }
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-2 mb-3">
            <div className="text-sm font-normal">Visitor</div>
            <ColorPicker
              value={workingStyleConfig.visitorBubbleBgColor ?? ""}
              onChange={(value) =>
                updateStyleConfig({ visitorBubbleBgColor: value })
              }
            />
            <ColorPicker
              value={workingStyleConfig.visitorBubbleTextColor ?? ""}
              onChange={(value) =>
                updateStyleConfig({ visitorBubbleTextColor: value })
              }
            />
            <ColorPicker
              value={workingStyleConfig.visitorBubbleBorderColor ?? ""}
              onChange={(value) =>
                updateStyleConfig({ visitorBubbleBorderColor: value })
              }
            />
          </div>
          <div className="grid grid-cols-3 items-center gap-4 mb-3">
            <div className="text-sm font-normal">System Text</div>
            <ColorPicker
              value={workingStyleConfig.systemTextColor ?? "#777470"}
              onChange={(value) =>
                updateStyleConfig({ systemTextColor: value })
              }
            />
            <div />
          </div>

          <div className="my-4 border-t border-slate-200" />
          <div className="text-sm font-medium mb-2">Button Styles</div>
          <div className="flex justify-between items-center">
            <div className="text-sm font-normal">Button Radius</div>
            <div className="flex items-center gap-1">
              <input
                type="number"
                className="border border-slate-300 rounded-md p-2 w-16 text-sm"
                value={workingStyleConfig.buttonRadius ?? 6}
                onChange={(e) => {
                  let value = parseInt(e.target.value);
                  if (isNaN(value)) value = 6;
                  if (value > 24) value = 24;
                  if (value < 0) value = 0;
                  updateStyleConfig({ buttonRadius: value });
                }}
                min={0}
                max={24}
              />
              <span className="text-xs text-slate-400">px</span>
            </div>
          </div>
          <Slider
            min={0}
            max={24}
            value={workingStyleConfig.buttonRadius ?? 6}
            onChange={(value) => updateStyleConfig({ buttonRadius: value })}
          />

          <div className="grid grid-cols-3 gap-2 items-center text-xs text-slate-400 mt-3 mb-1">
            <div></div>
            <div>Color</div>
            <div>Text Color</div>
          </div>
          <div className="grid grid-cols-3 items-center gap-2 mb-3">
            <div className="text-sm font-normal">Primary</div>
            <ColorPicker
              value={workingStyleConfig.primaryButtonColor ?? "#1670E7"}
              onChange={(value) =>
                updateStyleConfig({ primaryButtonColor: value })
              }
            />
            <ColorPicker
              value={workingStyleConfig.primaryButtonTextColor ?? "#FFFFFF"}
              onChange={(value) =>
                updateStyleConfig({ primaryButtonTextColor: value })
              }
            />
          </div>
          <div className="grid grid-cols-4 gap-2 items-center text-xs text-slate-400 mb-1">
            <div></div>
            <div>Color</div>
            <div>Text Color</div>
            <div>Border</div>
          </div>
          <div className="grid grid-cols-4 items-center gap-2 mb-3">
            <div className="text-sm font-normal">Secondary</div>
            <ColorPicker
              value={workingStyleConfig.secondaryButtonColor ?? "#FFFFFF"}
              onChange={(value) =>
                updateStyleConfig({ secondaryButtonColor: value })
              }
            />
            <ColorPicker
              value={workingStyleConfig.secondaryButtonTextColor ?? "#64748B"}
              onChange={(value) =>
                updateStyleConfig({ secondaryButtonTextColor: value })
              }
            />
            <ColorPicker
              value={workingStyleConfig.secondaryButtonBorderColor ?? ""}
              onChange={(value) =>
                updateStyleConfig({ secondaryButtonBorderColor: value })
              }
            />
          </div>
        </div>
      </Accordion.Body>
    </Accordion.Item>
  );
}
